/**
 * BLRRD Path Editor — bezier.js  (Phase 4.G.vector, Wave 12.2)
 *
 * Cubic-Bezier evaluation + adaptive flattening + bounding box.
 *
 * Evaluation — de Casteljau (1959)
 * ────────────────────────────────
 * A cubic Bezier B(t) over control points p0..p3 is computed by repeated
 * linear interpolation:
 *
 *     a = lerp(p0, p1, t)    b = lerp(p1, p2, t)    c = lerp(p2, p3, t)
 *     d = lerp(a, b, t)      e = lerp(b, c, t)
 *     B(t) = lerp(d, e, t)
 *
 * This is numerically stable for t ∈ [0,1] (every lerp stays inside the
 * convex hull of the control polygon) and is the canonical reference for
 * the polynomial form  B(t) = (1-t)³p0 + 3(1-t)²t p1 + 3(1-t)t² p2 + t³p3
 * (Hughes/van Dam §15.3). We keep BOTH so the test gate can cross-check
 * de Casteljau against the analytic polynomial.
 *
 * Adaptive flattening — recursive subdivision (Hughes §15.6)
 * ──────────────────────────────────────────────────────────
 * A Bezier is "flat enough" to approximate by its chord p0→p3 when the
 * control points p1, p2 lie within `tolerance` pixels of that chord. If
 * not, split at t=0.5 (de Casteljau gives both halves' control points
 * for free) and recurse on each half. This produces fewer points on
 * straight runs and more on tight curves — far better than uniform-t
 * sampling at the same vertex budget.
 *
 * Provenance: clean-room from de Casteljau 1959 + Hughes/van Dam 3rd ed.
 * NO Inkscape/GIMP/Krita source consulted.
 */

import { pathSegments } from './model.js';

/* ── de Casteljau single-point evaluation ────────────────────────────── */

/**
 * Evaluate a cubic Bezier at parameter t via de Casteljau's algorithm.
 * Points may be {x,y} objects.
 *
 * @returns {{x:number,y:number}}
 */
export function evalCubic(p0, p1, p2, p3, t) {
    const ax = p0.x + (p1.x - p0.x) * t, ay = p0.y + (p1.y - p0.y) * t;
    const bx = p1.x + (p2.x - p1.x) * t, by = p1.y + (p2.y - p1.y) * t;
    const cx = p2.x + (p3.x - p2.x) * t, cy = p2.y + (p3.y - p2.y) * t;
    const dx = ax + (bx - ax) * t, dy = ay + (by - ay) * t;
    const ex = bx + (cx - bx) * t, ey = by + (cy - by) * t;
    return { x: dx + (ex - dx) * t, y: dy + (ey - dy) * t };
}

/**
 * Analytic (polynomial Bernstein-basis) cubic evaluation. Mathematically
 * identical to `evalCubic`; kept as an independent reference so the test
 * gate can verify de Casteljau without circular logic.
 *
 * @returns {{x:number,y:number}}
 */
export function evalCubicAnalytic(p0, p1, p2, p3, t) {
    const u = 1 - t;
    const w0 = u * u * u;
    const w1 = 3 * u * u * t;
    const w2 = 3 * u * t * t;
    const w3 = t * t * t;
    return {
        x: w0 * p0.x + w1 * p1.x + w2 * p2.x + w3 * p3.x,
        y: w0 * p0.y + w1 * p1.y + w2 * p2.y + w3 * p3.y,
    };
}

/* ── Subdivision (de Casteljau split at t=0.5) ───────────────────────── */

/**
 * Split a cubic at t into [left, right], each a 4-tuple of control
 * points. de Casteljau's intermediate points ARE the new control points.
 */
function splitCubic(p0, p1, p2, p3, t) {
    const ax = p0.x + (p1.x - p0.x) * t, ay = p0.y + (p1.y - p0.y) * t;
    const bx = p1.x + (p2.x - p1.x) * t, by = p1.y + (p2.y - p1.y) * t;
    const cx = p2.x + (p3.x - p2.x) * t, cy = p2.y + (p3.y - p2.y) * t;
    const dx = ax + (bx - ax) * t, dy = ay + (by - ay) * t;
    const ex = bx + (cx - bx) * t, ey = by + (cy - by) * t;
    const fx = dx + (ex - dx) * t, fy = dy + (ey - dy) * t;
    return {
        left:  [{ x: p0.x, y: p0.y }, { x: ax, y: ay }, { x: dx, y: dy }, { x: fx, y: fy }],
        right: [{ x: fx, y: fy }, { x: ex, y: ey }, { x: cx, y: cy }, { x: p3.x, y: p3.y }],
    };
}

/* ── Flatness test ───────────────────────────────────────────────────── */

/* Perpendicular distance from point (px,py) to the line through (ax,ay)
 * and (bx,by). When the chord is degenerate (a==b) we fall back to the
 * Euclidean distance to the shared endpoint. */
function distToLine(px, py, ax, ay, bx, by) {
    const dx = bx - ax, dy = by - ay;
    const lenSq = dx * dx + dy * dy;
    if (lenSq < 1e-12) {
        const ex = px - ax, ey = py - ay;
        return Math.sqrt(ex * ex + ey * ey);
    }
    /* |cross((P-A), (B-A))| / |B-A|. */
    const cross = (px - ax) * dy - (py - ay) * dx;
    return Math.abs(cross) / Math.sqrt(lenSq);
}

/**
 * "Flat enough": both interior control points within `tol` of the chord.
 */
function isFlat(p0, p1, p2, p3, tol) {
    const d1 = distToLine(p1.x, p1.y, p0.x, p0.y, p3.x, p3.y);
    const d2 = distToLine(p2.x, p2.y, p0.x, p0.y, p3.x, p3.y);
    return d1 <= tol && d2 <= tol;
}

/* ── Adaptive flattening of a single segment ─────────────────────────── */

function flattenSegment(p0, p1, p2, p3, tol, out, depth) {
    /* Hard recursion cap (defends against pathological / NaN handles). */
    if (depth >= 24 || isFlat(p0, p1, p2, p3, tol)) {
        out.push({ x: p3.x, y: p3.y });
        return;
    }
    const { left, right } = splitCubic(p0, p1, p2, p3, 0.5);
    flattenSegment(left[0],  left[1],  left[2],  left[3],  tol, out, depth + 1);
    flattenSegment(right[0], right[1], right[2], right[3], tol, out, depth + 1);
}

/* ── Public: flatten a whole path to a polyline ──────────────────────── */

/**
 * Flatten a path to a polyline (array of {x,y}). The first point is the
 * first node's anchor; each segment then contributes its interior +
 * endpoint vertices. A closed path's polyline ends back at the first
 * anchor (the closing segment terminates there).
 *
 * @param {{nodes:Array, closed:boolean}} path
 * @param {number} [tolerance=0.25]   max chord deviation in pixels
 * @returns {Array<{x:number,y:number}>}
 */
export function flattenPath(path, tolerance = 0.25) {
    const tol = Number.isFinite(tolerance) && tolerance > 0 ? tolerance : 0.25;
    const segs = pathSegments(path);
    if (segs.length === 0) return [];
    const out = [{ x: segs[0][0].x, y: segs[0][0].y }];
    for (const [p0, p1, p2, p3] of segs) {
        flattenSegment(p0, p1, p2, p3, tol, out, 0);
    }
    return out;
}

/* ── Bounding box ────────────────────────────────────────────────────── */

/**
 * Tight-ish bounding box of a path. We bound the FLATTENED polyline,
 * which is a superset-safe under-estimate of the true control-hull box
 * but a tight estimate of the actual curve (since flattening tracks the
 * curve to `tolerance`). Returns null for an empty path.
 *
 * @param {object} path
 * @param {number} [tolerance=0.25]
 * @returns {{minX:number,minY:number,maxX:number,maxY:number}|null}
 */
export function pathBounds(path, tolerance = 0.25) {
    const poly = flattenPath(path, tolerance);
    if (poly.length === 0) return null;
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const p of poly) {
        if (p.x < minX) minX = p.x;
        if (p.y < minY) minY = p.y;
        if (p.x > maxX) maxX = p.x;
        if (p.y > maxY) maxY = p.y;
    }
    return { minX, minY, maxX, maxY };
}

/**
 * Bounding box of the raw control polygon (anchors + handles). This is a
 * conservative over-estimate of the curve box — used by the rasteriser to
 * size its scanline window so no curve pixel is ever clipped.
 *
 * @param {object} path
 * @returns {{minX:number,minY:number,maxX:number,maxY:number}|null}
 */
export function controlBounds(path) {
    const n = path.nodes;
    if (!n || n.length === 0) return null;
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    const acc = (p) => {
        if (!p) return;
        if (p.x < minX) minX = p.x;
        if (p.y < minY) minY = p.y;
        if (p.x > maxX) maxX = p.x;
        if (p.y > maxY) maxY = p.y;
    };
    for (const node of n) { acc(node.anchor); acc(node.handleIn); acc(node.handleOut); }
    if (!Number.isFinite(minX)) return null;
    return { minX, minY, maxX, maxY };
}
